import React, { useState } from "react";
import { Form, Upload, Switch, message, Spin } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useUploadFile } from "../../../../../../hooks/file";
import { RecordingType } from "../../../../../../hooks/api/recordings";
import { useMiddahTableGet } from "../../../../../../hooks/api/middot";
import { LazySelect } from "../../../../../Common/SelectLazyLoader";

const { Dragger } = Upload;

interface RecordingFormProps {
  mode: "create" | "edit";
  initialTagOptions?: { label: string; value: string }[];
}

export default function RecordingForm({
  mode,
  initialTagOptions,
}: RecordingFormProps): React.ReactElement {
  const form = Form.useFormInstance();
  const uploadFileHook = useUploadFile("common/r2/upload-image", true, true);
  const middahTableGet = useMiddahTableGet();
  const [uploading, setUploading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const nameValue = Form.useWatch("name", form);
  const typeValue = Form.useWatch("type", form);
  const fileLinkValue = Form.useWatch("fileLink", form);
  const globalContentValue = Form.useWatch("globalContent", form);
  const middahsValue = Form.useWatch("middahs", form);

  const getRecordingType = (filename: string): RecordingType => {
    const ext = filename.split(".").pop()?.toLowerCase();
    return ext === "mp4" ? "video" : "audio";
  };

  const customUpload = async (options: any) => {
    const { file, onSuccess, onError } = options;
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);

    try {
      const response = await uploadFileHook.fetch(formData);

      if (response?.url) {
        message.success(`${file.name} uploaded successfully.`);
        setUploadedFileName(file.name);
        form.setFieldsValue({
          name: file.name,
          type: getRecordingType(file.name),
          fileLink: response.url,
        });
        onSuccess?.(response);
      } else {
        throw new Error("Upload failed: no URL in response");
      }
    } catch (error: any) {
      message.error(`${file.name} upload failed.`);
      setUploadedFileName(null);
      onError?.(error);
    } finally {
      setUploading(false);
    }
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".mp3,.mp4,.m4a",
    customRequest: customUpload,
    showUploadList: false,
  };

  // Показываем имя если есть что-то из: upload, form value, edit mode
  const displayName = uploadedFileName || nameValue || "";
  const showFileName = mode === "edit" || !!uploadedFileName || !!nameValue;

  const fetchMiddahOptions = async (params: {
    search?: string;
    page: number;
    pageSize: number;
  }) => {
    const response = await middahTableGet.fetch({
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
    });
    return response?.data || [];
  };

  const mapMiddahOption = (item: any) => ({
    label: item.name,
    value: item.id,
  });

  return (
    <>
      {mode === "create" && !uploadedFileName && !nameValue && (
        <Form.Item name="file" rules={[{ required: true }]}>
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              {uploading ? (
                <Spin />
              ) : (
                <InboxOutlined style={{ fontSize: 48, color: "#1890ff" }} />
              )}
            </p>
            <p style={{ fontSize: 16, marginBottom: 8 }}>
              {uploading
                ? "Uploading..."
                : "Click or drag file to this area to upload"}
            </p>
            <p style={{ color: "#999" }}>Upload .mp3, .mp4, .M4A files</p>
          </Dragger>
        </Form.Item>
      )}

      <Form.Item name="name" hidden>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name="type" hidden>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name="fileLink" hidden>
        <input type="hidden" />
      </Form.Item>

      {showFileName && (
        <Form.Item label="File Name">
          <div style={{ fontSize: 16, fontWeight: 500, padding: "8px 0" }}>
            {displayName}
          </div>
        </Form.Item>
      )}

      <Form.Item
        label="Tags"
        name="middahs"
        rules={[{ required: true, message: "Please select at least one tag" }]}
      >
        <LazySelect
          fetchOptions={fetchMiddahOptions}
          mapOption={mapMiddahOption}
          pageSize={20}
          mode="multiple"
          initialOptions={initialTagOptions}
        />
      </Form.Item>

      <div style={{ color: "#999", marginTop: -8, marginBottom: 24 }}>
        Add tags to categorize this content (Middot or general tags)
      </div>

      <Form.Item name="globalContent" initialValue={false}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Switch
            checked={!!globalContentValue}
            onChange={(checked) => {
              form.setFieldValue("globalContent", checked);
            }}
          />
          <span>Mark as Global Content</span>
          <span style={{ color: "#999" }}>(Accessible across all Vaads)</span>
        </div>
      </Form.Item>
    </>
  );
}
